import * as React from "react";
import { CircleAlert } from "./icons";

import { cn } from "utils/cn";

export interface EmptyStateProps {
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Usually one primary Button, optionally a secondary one. */
  action?: React.ReactNode;
  className?: string;
  /** Tighter spacing for use inside cards and tables. */
  compact?: boolean;
}

/** "Nothing here yet" panel: says what is missing and offers the next step. */
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  className,
  compact = false,
}) => (
  <div
    data-ui="empty-state"
    className={cn(
      "flex flex-col items-center justify-center text-center",
      compact ? "gap-2 px-4 py-8" : "gap-3 px-6 py-14",
      className
    )}
  >
    {icon && (
      <span className="mb-1 flex h-11 w-11 items-center justify-center rounded-card border border-border bg-surface-muted text-muted-foreground [&_svg]:h-5 [&_svg]:w-5">
        {icon}
      </span>
    )}
    <h3 className="text-base font-semibold tracking-tight text-foreground">{title}</h3>
    {description && <p className="max-w-sm text-sm text-muted-foreground">{description}</p>}
    {action && (
      <div className="mt-2 flex flex-wrap items-center justify-center gap-2">{action}</div>
    )}
  </div>
);

export type ErrorStateProps = Omit<EmptyStateProps, "icon">;

/** Something failed to load: explains what happened and offers a retry. */
export const ErrorState: React.FC<ErrorStateProps> = (props) => (
  <EmptyState {...props} icon={<CircleAlert className="text-danger" />} />
);
